import {
  invoiceStatus,
  invoiceStatusColor,
  invoiceStatusLabel,
  updateActionStatus,
  quotationStatus,
  quotationStatusColor,
  quotationStatusLabel,
  reportStatus,
  reportStatusColor,
  reportStatusLabel,
  requestFormStatus,
  requestFormStatusColor,
  requestFormStatusLabel,
  sampleStatus,
  sampleStatusColor,
  sampleStatusLabel,
  testPlanStatus,
  testPlanStatusColor,
  testPlanStatusLabel,
  DATE_TIME_FORMAT,
  fileNameType,
  DOCUMENT_TYPE,
  caseStatus,
} from "app/constants";
import {
  convertDateStringToJpFormat,
  defaultFormatNumberOfPeople,
  defaultFormatNumberOfSample,
  formatIndex,
} from "app/utils";
import React, { useCallback, useMemo, useState } from "react";
import ProjectTableInfo from "../ProjectTableInfo";
import InputText from "../../../UI/Form/Input/InputText";
import moment from "moment/moment";
import PrimaryBtn from "../../../UI/Button/PrimaryBtn";
import Popup from "../Popup";
import CaseStatusCell from "../TestScheduleTableInfo/CaseStatusCell";
import useCallApi from "../../../../pages/Project/useCallApi";
import UploadDocumentForm from "./UploadDocumentForm";
import { CustomWidthTooltip } from "../../../UI/Tooltip/CustomWidthTooltip";
import { commonServices } from "../../../../services/api";

function TestScheduleItem({
  item,
  scheduleNo,
  onUpdateSchedule,
  setDownloading,
}) {
  const [anchor, setAnchor] = useState(null);
  const [displayData, setDisplayData] = useState(item.caseInfos);
  const [currentPopupId, setCurrentPopupId] = useState();
  const [isOpenAgainModal, setIsOpenAgainModal] = useState(false);
  const [againComment, setAgainComment] = useState("");
  const [isOpenUploadFileModal, setIsOpenUploadFileModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState("");

  const updateData = (callback) => {
    onUpdateSchedule?.();
    setDisplayData(callback);
  };

  const {
    updateQuotation,
    updateRqForm,
    updateSampleStatus,
    updateTestPlanStatus,
    updateInvoiceStatus,
  } = useCallApi({
    setDisplayData: updateData,
    onClickReturn,
  });

  const handleClickPopup = useCallback(
    (event) => {
      setAnchor(anchor ? null : event.currentTarget);
    },
    [anchor]
  );

  const handleClickUpdateCell = useCallback(
    (e, id) => {
      setCurrentPopupId(id);
      e.preventDefault();
      handleClickPopup(e);
    },
    [handleClickPopup]
  );


  const getFileExtension = (file) => {
    if (!file) return "";

    if (typeof file.fileType === "string") {
      const ext = file.fileType.replace(/^\./, "").toLowerCase();
      if (/^[a-z0-9]+$/.test(ext)) return ext;
    }

    if (file.fileName) {
      const ext = file.fileName.split(".").pop()?.toLowerCase();
      if (ext && ext !== file.fileName) return ext;
    }

    if (file.fileUrl) {
      const ext = file.fileUrl.split(".").pop()?.toLowerCase().split("?")[0];
      if (ext && ext !== file.fileUrl) return ext;
    }

    return "";
  };

  const downloadFileByType = (file, downloadName) => {
    const extension = getFileExtension(file);
    if (extension === "zip") {
      return commonServices.downloadWithFileId(
        file._id,
        extension,
        downloadName
      );
    }

    return commonServices.downloadPDFWithFileId(file._id, downloadName);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleDownloadFile(files, filename) {
    if (!Array.isArray(files) || files.length === 0) {
      return;
    }

    const timeStamp = moment().format("YYYYMMDDHHmmss");
    setDownloading(true);

    Promise.all(
      files.map(
        (file, index) =>
          new Promise((resolve) => {
            setTimeout(() => {
              let name = '';

              if (file.fileName) {
                name = file.fileName ? getBaseName(file.fileName) : null;
              } else {
                name = files.length > 1
                  ? `${filename}_${formatIndex(index + 1)}_${timeStamp}`
                  : `${filename}_${timeStamp}`;
              }

              downloadFileByType(file, name).finally(resolve);
            }, index * 500);
          })
      )
    ).finally(() => setDownloading(false));
  }
  const getBaseName = (filename) => {
    if (!filename) return "";
    return filename.substring(0, filename.lastIndexOf(".")) || filename;
  };

  const onLeftClick = useCallback(
    (name, status, files, caseId, customer) => {
      const companyName = customer?.companyNameKanji || "";
      let filename = `${fileNameType[name]}_${caseId}_${companyName}`;
      if (
        (name === "quotation" &&
          (status === quotationStatus.submitted ||
            status === quotationStatus.lost)) ||
        (name === "requestForm" &&
          (status === requestFormStatus.confirmed ||
            status === requestFormStatus.rejected ||
            status === requestFormStatus.submitted)) ||
        (name === "testPlan" &&
          (status === testPlanStatus.submitted ||
            status === testPlanStatus.confirmed)) ||
        (name === "report" &&
          (status === reportStatus.submitted ||
            status === reportStatus.confirmed))
      ) {
        handleDownloadFile(Array.isArray(files) ? files : [files], filename);
      }
    },
    [handleDownloadFile]
  );

  const onDownloadInvoice = useCallback(
    ({ billFiles, deliveryFiles, caseId, customer }) => {
      const companyName = customer?.companyNameKanji || "";
      let filenameBill = `${fileNameType.invoice}_${caseId}_${companyName}`;
      let filenameDelivery = `${fileNameType.delivery}_${caseId}_${companyName}`;

      handleDownloadFile(Array.isArray(billFiles) ? billFiles : [billFiles], filenameBill);
      handleDownloadFile(Array.isArray(deliveryFiles) ? deliveryFiles : [deliveryFiles], filenameDelivery);
    },
    [handleDownloadFile]
  );

  function onClickReturn() {
    setAnchor(null);
    setCurrentPopupId(null);
    setIsOpenUploadFileModal(false);
  }

  const tableData = useMemo(() => {
    return displayData.map((item, index) => {
      const disablePopup =
        item?.caseStatus === caseStatus.completed ||
        item?.caseStatus === caseStatus.lost;

      return {
        item: item,
        caseId: item.reportInfo?.testId || item.quotationInfo?.quotationId,
        caseStatus: item.caseStatus,
        customerId: (
          <div style={{ width: "calc(100% - 3px)" }}>
            <CustomWidthTooltip
              title={item?.customer?.customerId || "No ID available"}
              arrow
              placement="bottom"
            >
              <span className="cursor-pointer">
                {item?.customer?.customerId || ""}
              </span>
            </CustomWidthTooltip>
          </div>
        ),
        quotation: (
          <div id={item?._id}>
            <button
              style={{
                color: quotationStatusColor[item.quotationStatus],
                cursor: "pointer",
              }}
              onClick={() =>
                onLeftClick(
                  "quotation",
                  item.quotationStatus,
                  item.quotationInfo?.fileInfo,
                  item.reportInfo?.testId || item.quotationInfo?.quotationId,
                  item.customer
                )
              }
              onContextMenu={(e) => {
                if (item.quotationStatus === quotationStatus.notStarted) {
                  return;
                }
                if (disablePopup) return;
                handleClickUpdateCell(e, `quotation-${item._id}`);
              }}
            >
              {quotationStatusLabel[item.quotationStatus]}
            </button>
            <Popup
              id={`quotation-${item._id}`}
              anchor={anchor}
              open={
                `quotation-${item._id}` === currentPopupId &&
                (item.quotationStatus === quotationStatus.lost ||
                  item.quotationStatus === quotationStatus.submitted)
              }
              onClickOutside={onClickReturn}
            >
              {!isOpenUploadFileModal &&
                item.quotationStatus === quotationStatus.submitted && (
                  <div className="flex gap-2">
                    <PrimaryBtn
                      text={updateActionStatus.upload}
                      onClick={() => setIsOpenUploadFileModal(true)}
                    />
                    <PrimaryBtn
                      text={updateActionStatus.lostOrder}
                      onClick={() =>
                        updateQuotation({
                          id: item._id,
                          index,
                          changeValue: quotationStatus.lost,
                        })
                      }
                    />
                    <PrimaryBtn
                      text={updateActionStatus.return}
                      onClick={onClickReturn}
                    />
                  </div>
                )}
              {!isOpenUploadFileModal &&
                item.quotationStatus === quotationStatus.lost && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateQuotationTime ||
                        item.quotationInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        disabled={disablePopup}
                        text={updateActionStatus.upload}
                        onClick={() => setIsOpenUploadFileModal(true)}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.recovery}
                        onClick={() =>
                          updateQuotation({
                            id: item._id,
                            index,
                            changeValue: quotationStatus.submitted,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {isOpenUploadFileModal && (
                <div>
                  <UploadDocumentForm
                    documentType={DOCUMENT_TYPE.QUOTATION}
                    caseInfoId={item?._id}
                    caseId={
                      item.reportInfo?.testId || item.quotationInfo?.quotationId
                    }
                    customerId={item?.customer?.customerId}
                    fileInfo={[]}
                    onCloseModal={onClickReturn}
                    setIsOpenUploadFileModal={setIsOpenUploadFileModal}
                    text={'見積り'}
                  />
                </div>
              )}
            </Popup>
          </div>
        ),
        requestForm: (
          <div>
            <button
              style={{
                color: requestFormStatusColor[item.requestFormStatus],
                cursor: "pointer",
              }}
              onClick={() => {
                if (!item.requestFormInfo) return;
                onLeftClick(
                  "requestForm",
                  item.requestFormStatus,
                  item.requestFormInfo?.fileInfo,
                  item.reportInfo?.testId || item.quotationInfo?.quotationId,
                  item.customer
                );
              }}
              onContextMenu={(e) => {
                if (disablePopup) return;
                if (
                  [
                    requestFormStatus.notStarted,
                    requestFormStatus.confirmed,
                    requestFormStatus.rejected,
                  ].includes(item.requestFormStatus)
                ) {
                  handleClickUpdateCell(e, `requestForm-${item._id}`);
                }
              }}
            >
              {requestFormStatusLabel[item.requestFormStatus]}
            </button>
            <Popup
              id={`requestForm-${item._id}`}
              anchor={anchor}
              open={
                `requestForm-${item._id}` === currentPopupId &&
                [
                  requestFormStatus.notStarted,
                  requestFormStatus.confirmed,
                  requestFormStatus.rejected,
                  requestFormStatus.waiting,
                ].includes(item.requestFormStatus)
              }
              onClickOutside={onClickReturn}
            >
              {!isOpenAgainModal &&
                item.requestFormStatus === requestFormStatus.notStarted && (
                  <div className="flex gap-2">
                    <PrimaryBtn
                      text={updateActionStatus.waiting}
                      onClick={() => {
                        updateRqForm({
                          id: item._id,
                          changeValue: requestFormStatus.waiting,
                          data: {
                            emailAddress: item?.customer?.email,
                          },
                          index,
                        });
                      }}
                    />
                    <PrimaryBtn
                      text={updateActionStatus.return}
                      onClick={onClickReturn}
                    />
                  </div>
                )}
              {!isOpenAgainModal &&
                item.requestFormStatus === requestFormStatus.confirmed && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateOrderTime ||
                        item?.requestFormInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.approval}
                        onClick={() => {
                          // setCurrentPopupId(null);
                          updateRqForm({
                            id: item._id,
                            changeValue: requestFormStatus.submitted,
                            index,
                          });
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.again}
                        onClick={() => {
                          setIsOpenAgainModal(true);
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.waiting}
                        onClick={() => {
                          updateRqForm({
                            id: item._id,
                            changeValue: requestFormStatus.waiting,
                            data: {
                              emailAddress: item?.customer?.email,
                            },
                            index,
                          });
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {!isOpenAgainModal &&
                item.requestFormStatus === requestFormStatus.rejected && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateOrderTime ||
                        item?.requestFormInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.confirmation}
                        onClick={() => {
                          updateRqForm({
                            id: item._id,
                            changeValue: requestFormStatus.confirmed,
                            index,
                          });
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.waiting}
                        onClick={() => {
                          updateRqForm({
                            id: item._id,
                            changeValue: requestFormStatus.waiting,
                            data: {
                              emailAddress: item?.customer?.email,
                            },
                            index,
                          });
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {!isOpenAgainModal &&
                item.requestFormStatus === requestFormStatus.waiting && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateOrderTime ||
                        item?.requestFormInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.confirmation}
                        onClick={() => {
                          updateRqForm({
                            id: item._id,
                            changeValue: requestFormStatus.confirmed,
                            index,
                          });
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.again}
                        onClick={() => {
                          setIsOpenAgainModal(true);
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {isOpenAgainModal && (
                <div>
                  <div className="mb-2">再対応依頼 コメント</div>
                  <InputText
                    onChange={(value) => {
                      setAgainComment(value);
                    }}
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <PrimaryBtn
                      text={"キャンセル"}
                      onClick={() => {
                        setIsOpenAgainModal(false);
                        setAgainComment("");
                      }}
                    />
                    <PrimaryBtn
                      text={"送信"}
                      onClick={() => {
                        setCurrentPopupId(null);
                        setIsOpenAgainModal(false);
                        setAgainComment("");
                        updateRqForm({
                          id: item._id,
                          changeValue: requestFormStatus.rejected,
                          data: {
                            comment: againComment,
                            emailAddress: item?.customer?.email,
                          },
                          index,
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>
          </div>
        ),
        testPlan: (
          <div>
            <button
              style={{
                color: testPlanStatusColor[item.testPlanStatus],
                cursor: "pointer",
              }}
              onClick={() => {
                onLeftClick(
                  "testPlan",
                  item.testPlanStatus,
                  item.testPlanInfo?.fileInfo,
                  item.reportInfo?.testId || item.quotationInfo?.quotationId,
                  item.customer
                );
              }}
              onContextMenu={(e) => {
                if (disablePopup) return;
                handleClickUpdateCell(e, `testPlan-${item._id}`);
              }}
            >
              {testPlanStatusLabel[item.testPlanStatus]}
            </button>
            <Popup
              id={`testPlan-${item._id}`}
              anchor={anchor}
              open={currentPopupId === `testPlan-${item._id}`}
              onClickOutside={onClickReturn}
            >
              <>
                {!isOpenUploadFileModal &&
                  item.testPlanStatus === testPlanStatus.rejected && (
                    <div className="flex flex-col gap-2">
                      <p>
                        最終更新日:
                        {moment(
                          item?.updatePlanTime || item?.testPlanInfo?.updatedAt
                        ).format(DATE_TIME_FORMAT)}
                      </p>
                      <div className="flex gap-2">
                        <PrimaryBtn
                          text={updateActionStatus.upload}
                          onClick={() => setIsOpenUploadFileModal(true)}
                        />
                        <PrimaryBtn
                          text={updateActionStatus.return}
                          onClick={onClickReturn}
                        />
                      </div>
                    </div>
                  )}
                {!isOpenUploadFileModal &&
                  item.testPlanStatus === testPlanStatus.confirmed && (
                    <div className="flex flex-col gap-2">
                      <p>
                        最終更新日:
                        {moment(
                          item?.updatePlanTime || item?.testPlanInfo?.updatedAt
                        ).format(DATE_TIME_FORMAT)}
                      </p>
                      <div className="flex gap-2">
                        <PrimaryBtn
                          text={updateActionStatus.upload}
                          onClick={() => setIsOpenUploadFileModal(true)}
                        />
                        <PrimaryBtn
                          text={updateActionStatus.clear}
                          onClick={() =>
                            updateTestPlanStatus({
                              id: item._id,
                              changeValue: testPlanStatus.none,
                              index,
                            })
                          }
                        />
                        <PrimaryBtn
                          text={updateActionStatus.return}
                          onClick={onClickReturn}
                        />
                      </div>
                    </div>
                  )}

                {!isOpenUploadFileModal &&
                  item.testPlanStatus === testPlanStatus.submitted && (
                    <div className="flex flex-col gap-2">
                      <p>
                        最終更新日:
                        {moment(
                          item?.updatePlanTime || item.testPlanInfo?.updatedAt
                        ).format(DATE_TIME_FORMAT)}
                      </p>
                      <div className="flex gap-2">
                        <PrimaryBtn
                          text={updateActionStatus.upload}
                          onClick={() => setIsOpenUploadFileModal(true)}
                        />
                        <PrimaryBtn
                          text={updateActionStatus.clear}
                          onClick={() =>
                            updateTestPlanStatus({
                              id: item._id,
                              changeValue: testPlanStatus.none,
                              index,
                            })
                          }
                        />
                        <PrimaryBtn
                          text={updateActionStatus.return}
                          onClick={onClickReturn}
                        />
                      </div>
                    </div>
                  )}
                {!isOpenUploadFileModal &&
                  item.testPlanStatus === testPlanStatus.notStarted && (
                    <div className="flex gap-2 mx-auto">
                      <PrimaryBtn
                        text={updateActionStatus.upload}
                        onClick={() => setIsOpenUploadFileModal(true)}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.clear}
                        onClick={() =>
                          updateTestPlanStatus({
                            id: item._id,
                            changeValue: testPlanStatus.none,
                            index,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  )}
                {!isOpenUploadFileModal &&
                  item.testPlanStatus === testPlanStatus.none && (
                    <div className="flex gap-2 mx-auto">
                      <PrimaryBtn
                        text={updateActionStatus.notStarted}
                        onClick={() =>
                          updateTestPlanStatus({
                            id: item._id,
                            changeValue: testPlanStatus.notStarted,
                            index,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  )}
                {isOpenUploadFileModal && (
                  <div>
                    <UploadDocumentForm
                      documentType={DOCUMENT_TYPE.TEST_PLAN}
                      caseInfoId={item?._id}
                      caseId={
                        item.reportInfo?.testId ||
                        item.quotationInfo?.quotationId
                      }
                      customerId={item?.customer?.customerId}
                      fileInfo={[]}
                      onCloseModal={onClickReturn}
                      setIsOpenUploadFileModal={setIsOpenUploadFileModal}
                      text={'計画書'}
                    />
                  </div>
                )}
              </>
            </Popup>
          </div>
        ),
        sampleStatus: (
          <div>
            <button
              style={{
                color: sampleStatusColor[item.sampleStatus],
                cursor: "pointer",
              }}
              onContextMenu={(e) => {
                if (disablePopup) return;
                if (item.sampleStatus === sampleStatus.submitted) return;
                handleClickUpdateCell(e, `sampleStatus-${item._id}`);
              }}
            >
              {sampleStatusLabel[item.sampleStatus]}
            </button>
            <Popup
              id={`sampleStatus-${item._id}`}
              anchor={anchor}
              open={
                currentPopupId === `sampleStatus-${item._id}` &&
                [
                  sampleStatus.confirmed,
                  sampleStatus.notStarted,
                  sampleStatus.rejected,
                ].includes(item.sampleStatus)
              }
              onClickOutside={onClickReturn}
            >
              {!isOpenAgainModal &&
                item.sampleStatus === sampleStatus.notStarted && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {item?.updateSampleTime
                        ? moment(item?.updateSampleTime).format(
                          DATE_TIME_FORMAT
                        )
                        : null}
                    </p>
                    <div className="flex gap-2 mx-auto">
                      <PrimaryBtn
                        text={updateActionStatus.confirmation}
                        onClick={() =>
                          updateSampleStatus({
                            id: item._id,
                            changeValue: sampleStatus.confirmed,
                            index,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {!isOpenAgainModal &&
                item.sampleStatus === sampleStatus.confirmed && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(item?.updateSampleTime).format(DATE_TIME_FORMAT)}
                    </p>
                    <p>
                      送り状・問合せ番号:{item?.sampleInquiryNumber}
                    </p>
                    <p>
                      備考:{item?.sampleNote}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.approval}
                        onClick={() =>
                          updateSampleStatus({
                            id: item._id,
                            changeValue: sampleStatus.submitted,
                            index,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.again}
                        onClick={() => {
                          setIsOpenAgainModal(true);
                        }}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {!isOpenAgainModal &&
                item.sampleStatus === sampleStatus.rejected && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(item?.updateSampleTime).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2 mx-auto">
                      <PrimaryBtn
                        text={updateActionStatus.confirmation}
                        onClick={() =>
                          updateSampleStatus({
                            id: item._id,
                            changeValue: sampleStatus.confirmed,
                            index,
                          })
                        }
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {isOpenAgainModal && (
                <div>
                  <div className="mb-2">再対応依頼 コメント</div>
                  <InputText
                    onChange={(value) => {
                      setAgainComment(value);
                    }}
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <PrimaryBtn
                      text={"キャンセル"}
                      onClick={() => {
                        setIsOpenAgainModal(false);
                        setAgainComment("");
                      }}
                    />
                    <PrimaryBtn
                      text={"送信"}
                      onClick={() => {
                        setCurrentPopupId(null);
                        setIsOpenAgainModal(false);
                        setAgainComment("");
                        updateSampleStatus({
                          id: item._id,
                          changeValue: sampleStatus.rejected,
                          data: {
                            comment: againComment,
                            emailAddress: item?.customer?.email,
                          },
                          index,
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>
          </div>
        ),
        report: (
          <div>
            <button
              disabled={disablePopup}
              style={{
                color: reportStatusColor[item.reportStatus],
                cursor: "pointer",
              }}
              onClick={() =>
                onLeftClick(
                  "report",
                  item.reportStatus,
                  item.reportInfo?.fileInfo,
                  item.reportInfo?.testId || item.quotationInfo?.quotationId,
                  item.customer
                )
              }
              onContextMenu={(e) => {
                if (disablePopup) return;
                handleClickUpdateCell(e, `report-${item._id}`);
              }}
            >
              {reportStatusLabel[item.reportStatus]}
            </button>
            <Popup
              id={`report-${item._id}`}
              anchor={anchor}
              open={`report-${item._id}` === currentPopupId}
              onClickOutside={onClickReturn}
            >
              {!isOpenUploadFileModal &&
                (item.reportStatus === reportStatus.confirmed ||
                  item.reportStatus === reportStatus.rejected) && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateReportTime || item?.reportInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.upload}
                        onClick={() => setIsOpenUploadFileModal(true)}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}

              {!isOpenUploadFileModal &&
                item.reportStatus === reportStatus.submitted && (
                  <div className="flex flex-col gap-2">
                    <p>
                      最終更新日:
                      {moment(
                        item?.updateReportTime || item?.reportInfo?.updatedAt
                      ).format(DATE_TIME_FORMAT)}
                    </p>
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.upload}
                        onClick={() => setIsOpenUploadFileModal(true)}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {!isOpenUploadFileModal &&
                item.reportStatus === reportStatus.notStarted && (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <PrimaryBtn
                        text={updateActionStatus.upload}
                        onClick={() => setIsOpenUploadFileModal(true)}
                      />
                      <PrimaryBtn
                        text={updateActionStatus.return}
                        onClick={onClickReturn}
                      />
                    </div>
                  </div>
                )}
              {isOpenUploadFileModal && (
                <div>
                  <UploadDocumentForm
                    documentType={DOCUMENT_TYPE.REPORT}
                    caseInfoId={item?._id}
                    caseId={
                      item.reportInfo?.testId || item.quotationInfo?.quotationId
                    }
                    customerId={item?.customer?.customerId}
                    fileInfo={[]}
                    onCloseModal={onClickReturn}
                    setIsOpenUploadFileModal={setIsOpenUploadFileModal}
                    text={'報告書'}
                  />
                </div>
              )}
            </Popup>
          </div>
        ),
        invoice: (
          <div>
            <button
              style={{
                color: invoiceStatusColor[item.invoiceStatus],
                cursor: "pointer",
              }}
              onClick={() => {
                if (
                  item.invoiceStatus === invoiceStatus.submitted ||
                  item.invoiceStatus === invoiceStatus.confirmed ||
                  item.invoiceStatus === invoiceStatus.rejected
                ) {
                  return onDownloadInvoice({
                    billFiles: item.invoiceInfo?.fileInfo,
                    deliveryFiles: item.invoiceInfo?.slipExcelFileInfo,
                    caseId:
                      item.reportInfo?.testId ||
                      item.quotationInfo?.quotationId,
                    customer: item.customer,
                  });
                }
              }}
              onContextMenu={(e) => {
                if (disablePopup) return;
                handleClickUpdateCell(e, `invoice-${item._id}`);
              }}
            >
              {invoiceStatusLabel[item.invoiceStatus]}
            </button>
            <Popup
              id={`invoice-${item._id}`}
              anchor={anchor}
              open={`invoice-${item._id}` === currentPopupId}
              onClickOutside={onClickReturn}
            >
              {!isOpenUploadFileModal &&
                (item.invoiceStatus === invoiceStatus.submitted ||
                  item.invoiceStatus === invoiceStatus.rejected) && (
                  <div className="flex flex-col">
                    <div className="flex flex-col gap-2">
                      <p>
                        最終更新日:
                        {moment(item.invoiceInfo?.updatedAt).format(
                          DATE_TIME_FORMAT
                        )}
                      </p>
                      <div className="flex gap-2">
                        <PrimaryBtn
                          text="請求書を選択"
                          onClick={() => {
                            setInvoiceType(DOCUMENT_TYPE.INVOICE);
                            setIsOpenUploadFileModal(true);
                          }}
                        />
                        <PrimaryBtn
                          text="納品書を選択"
                          onClick={() => {
                            setInvoiceType(DOCUMENT_TYPE.INVOICE_DELIVERY);
                            setIsOpenUploadFileModal(true);
                          }}
                        />
                        <PrimaryBtn
                          text={updateActionStatus.return}
                          onClick={onClickReturn}
                        />
                      </div>
                    </div>
                  </div>
                )}
              {!isOpenUploadFileModal &&
                (item.invoiceStatus === invoiceStatus.notStarted ||
                  item.invoiceStatus === invoiceStatus.confirmed) && (
                  <div className="flex gap-2 mx-auto">
                    <PrimaryBtn
                      text="請求書を選択"
                      onClick={() => {
                        setInvoiceType(DOCUMENT_TYPE.INVOICE);
                        setIsOpenUploadFileModal(true);
                      }}
                    />
                    <PrimaryBtn
                      text="納品書を選択"
                      onClick={() => {
                        setInvoiceType(DOCUMENT_TYPE.INVOICE_DELIVERY);
                        setIsOpenUploadFileModal(true);
                      }}
                    />
                    <PrimaryBtn
                      text={updateActionStatus.clear}
                      onClick={() =>
                        updateInvoiceStatus({
                          id: item._id,
                          changeValue: invoiceStatus.none,
                          index,
                        })
                      }
                    />
                    <PrimaryBtn
                      text={updateActionStatus.return}
                      onClick={onClickReturn}
                    />
                  </div>
                )}
              {!isOpenUploadFileModal &&
                item.invoiceStatus === invoiceStatus.none && (
                  <div className="flex gap-2 mx-auto">
                    <PrimaryBtn
                      text={updateActionStatus.notStarted}
                      onClick={() =>
                        updateInvoiceStatus({
                          id: item._id,
                          changeValue: invoiceStatus.notStarted,
                          index,
                        })
                      }
                    />
                    <PrimaryBtn
                      text={updateActionStatus.return}
                      onClick={onClickReturn}
                    />
                  </div>
                )}
              {isOpenUploadFileModal &&
                invoiceType === DOCUMENT_TYPE.INVOICE && (
                  <div>
                    <UploadDocumentForm
                      documentType={invoiceType}
                      caseInfoId={item?._id}
                      caseId={
                        item.reportInfo?.testId ||
                        item.quotationInfo?.quotationId
                      }
                      customerId={item?.customer?.customerId}
                      fileInfo={[]}
                      onCloseModal={onClickReturn}
                      setIsOpenUploadFileModal={setIsOpenUploadFileModal}
                      text={'請求書'}
                    />
                  </div>
                )}
              {isOpenUploadFileModal &&
                invoiceType === DOCUMENT_TYPE.INVOICE_DELIVERY && (
                  <div>
                    <UploadDocumentForm
                      documentType={invoiceType}
                      caseInfoId={item?._id}
                      caseId={
                        item.reportInfo?.testId ||
                        item.quotationInfo?.quotationId
                      }
                      customerId={item?.customer?.customerId}
                      fileInfo={[]}
                      onCloseModal={onClickReturn}
                      setIsOpenUploadFileModal={setIsOpenUploadFileModal}
                      text={'納品書'}
                    />
                  </div>
                )}
            </Popup>
          </div>
        ),
        samplePeopleNumber: defaultFormatNumberOfPeople(
          item.samplePeopleNumber
        ),
        sampleNumber: defaultFormatNumberOfSample(item.sampleNumber),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    anchor,
    currentPopupId,
    displayData,
    handleClickUpdateCell,
    onDownloadInvoice,
    onLeftClick,
    updateQuotation,
    updateRqForm,
    updateSampleStatus,
  ]);

  return (
    <div className="overflow-x-auto mt-8">
      <p id={item.caseId} className="text-custom-size text-green mb-3">
        スケジュール{scheduleNo}：{convertDateStringToJpFormat(item.testDate)}
      </p>

      <div>
        <ProjectTableInfo
          tableConfig={[
            {
              title: "案件ID",
              width: "11.74%",
              dataKey: "caseId",
              textAlign: "center",
            },
            {
              title: "見積り",
              width: "7.47%",
              dataKey: "quotation",
              textAlign: "center",
            },
            {
              title: "依頼書",
              width: "7.47%",
              dataKey: "requestForm",
              textAlign: "center",
            },
            {
              title: "計画書",
              width: "7.47%",
              dataKey: "testPlan",
              textAlign: "center",
            },
            {
              title: "サンプル",
              width: "8%",
              dataKey: "sampleStatus",
              textAlign: "center",
            },
            {
              title: "報告書",
              width: "7.47%",
              dataKey: "report",
              textAlign: "center",
            },
            {
              title: "請求書",
              width: "7.47%",
              dataKey: "invoice",
              textAlign: "center",
            },
            {
              title: "顧客ID",
              width: "22.41%",
              dataKey: "customerId",
              textAlign: "center",
            },
            {
              title: "人数",
              width: "6.4%",
              dataKey: "samplePeopleNumber",
              textAlign: "center",
            },
            {
              title: "検体数",
              width: "6.4%",
              dataKey: "sampleNumber",
              textAlign: "center",
            },
            {
              title: "状況",
              width: "7.68%",
              dataKey: "caseStatus",
              textAlign: "center",
              CustomTableCell: CaseStatusCell,
            },
          ]}
          tableData={tableData}
        />
      </div>
    </div>
  );
}

export default TestScheduleItem;
